import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import useOnCloseWithConfirmation from '@/hooks/useOnCloseWithConfirmation';
import { MLProject } from '@/types/resources';
import { api } from '@/utils/api';
import { productionTeams } from '@/utils/data';
import { handleFormErrors } from '@/utils/helpers';

type FormDataT = {
  video: string;
  video_duration?: number;
  video_width?: number;
  video_height?: number;
  aspect_ratio?: string;
  thumbnail: string;
  title: string;
  project_id: string;
  description: string;
  fresh_id: string;
  production_team: string;
  notes: string;
};

interface Props {
  onClose: () => void;
  editableProject?: MLProject;
}

const Form: FC<Props> = ({ onClose, editableProject }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    handleSubmit,
    setError,
    control,
    setValue,
    clearErrors,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      video: '',
      thumbnail: '',
      title: editableProject?.title ?? '',
      project_id: editableProject?.projectId ?? '',
      fresh_id: editableProject?.freshId ?? '',
      description: editableProject?.description ?? '',
      production_team: editableProject?.productionTeam ?? '',
      notes: editableProject?.notes ?? '',
    },
  });

  const onCancel = useOnCloseWithConfirmation(onClose, isDirty);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const { id } = editableProject
          ? await api.put<typeof data, { id: number }>(
              `/v1/distribution/ml-projects/${editableProject.id}`,
              data,
            )
          : await api.post<typeof data, { id: number }>(
              '/v1/distribution/ml-projects',
              data,
            );

        await queryClient.invalidateQueries(['ml_projects'], {
          exact: false,
        });

        if (editableProject) {
          onClose();
          return;
        }

        navigate(`../${id.toString()}`, { relative: 'path' });
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, true);
      }
    })(e);
  };

  const onVideoUpload = (file: File) => {
    function gcd(a: number, b: number): number {
      return b === 0 ? a : gcd(b, a % b);
    }

    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      const duration = Math.floor(video.duration);
      const width = video.videoWidth;
      const height = video.videoHeight;
      const ratio = gcd(width, height);
      const aspectRatio = `${width / ratio}:${height / ratio}`;

      setValue('video_duration', duration);
      setValue('video_width', width);
      setValue('video_height', height);
      setValue('aspect_ratio', aspectRatio);
    };

    video.src = URL.createObjectURL(file);
  };

  return (
    <Modal>
      <SidePanel
        heading={editableProject ? 'Edit Project' : 'New Project'}
        onClose={onCancel}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                id="video-input"
                control={control}
                existingMedia={editableProject?.video}
                label="Video"
                type="video"
                name="video"
                fileUploadedCallback={onVideoUpload}
                notes={
                  <>
                    Aspect ratio: 16:9
                    <br />
                    Max file size: 128GB or 12 hours
                  </>
                }
              />
              <FormField<FormDataT>
                id="thumb-input"
                control={control}
                existingMedia={editableProject?.thumbnail}
                label="Thumb"
                type="image"
                name="thumbnail"
                notes={
                  <>
                    Formats: JPG, PNG, GIF
                    <br />
                    Size: 1280 x 720 / Up to 10MB
                  </>
                }
              />
              <FormField<FormDataT>
                control={control}
                label="Title"
                name="title"
              />
              <FormField<FormDataT>
                control={control}
                label="Project ID"
                placeholder="Add ID"
                name="project_id"
              />
              <FormField<FormDataT>
                control={control}
                label="Fresh ID"
                placeholder="Add ID"
                name="fresh_id"
              />
              <FormField<FormDataT, string>
                control={control}
                options={productionTeams.map((t) => ({
                  label: t,
                  value: t,
                }))}
                name="production_team"
                label="Production Team"
                type="select"
              />
              <FormField<FormDataT>
                control={control}
                label="Description"
                type="textarea"
                name="description"
              />
              <FormField<FormDataT>
                control={control}
                label="Note"
                subLabel="(Internal Information)"
                type="textarea"
                name="notes"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {editableProject ? 'Update' : 'Save'}
            </Button>
            <Button type="button" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default Form;
